import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../../components/Modal';
import { Button, Input } from '../../../components';
import { useAuth } from '../../../pages/Auth/useAuth';

interface DeleteOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationName: string;
  onDelete: () => Promise<{ success: boolean; error?: string }>;
}

export const DeleteOrganizationModal: React.FC<DeleteOrganizationModalProps> = ({
  isOpen,
  onClose,
  organizationName,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiredText = `DELETE ${organizationName.toUpperCase()}`;
  const isConfirmed = confirmationText === requiredText;

  const handleDelete = async () => {
    if (!isConfirmed || isDeleting) return;

    setIsDeleting(true);
    setError(null);

    // Add a small delay to show the loading state before making the request
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      // IMPORTANT: The DELETE request needs the Organization ID header
      // ApiClient.getAuthHeaders() reads it from localStorage via authService.getStoredOrganizationId()
      // We must NOT clear localStorage/logout until AFTER the request completes successfully
      // The Organization ID is stored in localStorage with key 'organization_id'
      const result = await onDelete();

      if (result.success) {
        // Backend has already logged out the user and invalidated the session
        // Organization is marked as INACTIVE and deletion is in progress
        // Now it's safe to clear local data since the request completed successfully

        // Clear React Query cache
        queryClient.clear();

        // Clear all local cached data (localStorage, sessionStorage, etc.)
        // This includes the Organization ID, but that's fine since the request already completed
        localStorage.clear();
        sessionStorage.clear();

        // Clear local auth state (this also clears tokens, but request already completed)
        logout();

        // Redirect to login page
        // Use window.location.href for a full page reload to ensure all state is cleared
        window.location.href = '/login';
      } else {
        setError(result.error || t('organization.delete.error'));
        setIsDeleting(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('organization.delete.error'));
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmationText('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('organization.delete.title')} size="lg">
      <div className="relative space-y-6">
        {/* Overlay when deleting */}
        {isDeleting && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mb-2"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('organization.delete.deleting')}
              </p>
            </div>
          </div>
        )}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <span className="material-icons text-yellow-600 dark:text-yellow-400 mr-2">
              warning
            </span>
            <div className="flex-1">
              <strong className="text-yellow-800 dark:text-yellow-200 block mb-2">
                {t('organization.delete.warning')}
              </strong>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm mb-3">
                {t('organization.delete.warningDescription')}
              </p>
              <ul className="list-disc list-inside text-yellow-700 dark:text-yellow-300 text-sm space-y-1">
                <li>{t('organization.delete.warningItem1')}</li>
                <li>{t('organization.delete.warningItem2')}</li>
                <li>{t('organization.delete.warningItem3')}</li>
                <li>{t('organization.delete.warningItem4')}</li>
                <li>{t('organization.delete.warningItem5')}</li>
                <li>{t('organization.delete.warningItem6')}</li>
              </ul>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-3">
                {t('organization.delete.stripeNote')}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="confirmation-input"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('organization.delete.confirmationLabel', { name: requiredText })}
          </label>
          <Input
            id="confirmation-input"
            type="text"
            value={confirmationText}
            onChange={e => setConfirmationText(e.target.value)}
            placeholder={requiredText}
            disabled={isDeleting}
            errorText={
              confirmationText && !isConfirmed
                ? t('organization.delete.confirmationError', { name: requiredText })
                : undefined
            }
            className={confirmationText && !isConfirmed ? 'border-red-500' : ''}
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <span className="material-icons text-red-500 mr-2">error</span>
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button onClick={handleClose} variant="secondary" disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={!isConfirmed || isDeleting}
            variant="danger"
            loading={isDeleting}
          >
            {t('organization.delete.button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
